import { createTheme } from "@mui/material/styles";
import {
  tokens,
  MSI_BLUE_TEXTURED_CHIP_SX,
  MSI_BLUE_TEXTURED_CONTAINED_BUTTON_SX,
  MSI_BLUE_TEXTURED_FILL_SX,
  MSI_BLUE_TEXTURED_HOVER_BG_SX,
  MSI_BLUE_TEXTURED_MUI_LINEAR_PROGRESS_BAR_PRIMARY_SX,
  MSI_BLUE_TEXTURED_MUI_SLIDER_PRIMARY_ROOT_SX,
  MSI_BLUE_TEXTURED_MUI_SWITCH_ROOT_SX,
  msiBlueTexturedMuiToggleButtonPrimarySelectedSx,
} from "@healthdesk/shared-utils";

export const muiTheme = createTheme({
  palette: {
    primary: {
      main: tokens.colors.primary.light,
      light: "#3A67A6",
      dark: "#0D2F64",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: tokens.colors.secondary.light,
      light: "#4F9FDF",
      dark: "#0B5791",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#d32f2f",
      light: "#ef5350",
      dark: "#c62828",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#ed6c02",
      light: "#ff9800",
      dark: "#e65100",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#0288d1",
      light: "#03a9f4",
      dark: "#01579b",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
      contrastText: "#FFFFFF",
    },
    background: {
      default: tokens.colors.bgPage.light,
      paper: tokens.colors.bgCard.light,
    },
    text: {
      primary: tokens.colors.textPrimary.light,
      secondary: tokens.colors.textSecondary.light,
      disabled: tokens.colors.textDisabled.light,
    },
    divider: tokens.colors.border.light,
  },
  typography: {
    fontFamily: tokens.typography.fontFamilyBody,
    caption: { fontSize: tokens.typography.size.caption },
    body2: { fontSize: tokens.typography.size.dense },
    body1: { fontSize: tokens.typography.size.body },
    h6: {
      fontSize: tokens.typography.size.subhead,
      fontFamily: tokens.typography.fontFamilyHeading,
    },
    h5: {
      fontSize: tokens.typography.size.heading,
      fontFamily: tokens.typography.fontFamilyHeading,
    },
    h4: {
      fontSize: tokens.typography.size.display,
      fontFamily: tokens.typography.fontFamilyHeading,
    },
    h3: {
      fontSize: tokens.typography.size.display,
      fontFamily: tokens.typography.fontFamilyHeading,
    },
    h2: {
      fontSize: tokens.typography.size.display,
      fontFamily: tokens.typography.fontFamilyHeading,
    },
    h1: {
      fontSize: tokens.typography.size.display,
      fontFamily: tokens.typography.fontFamilyHeading,
    },
  },
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          minHeight: tokens.spacing.touchTarget,
          minWidth: tokens.spacing.touchTarget,
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          minHeight: tokens.spacing.touchTarget,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          ...MSI_BLUE_TEXTURED_CONTAINED_BUTTON_SX,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        filledPrimary: {
          ...MSI_BLUE_TEXTURED_CHIP_SX,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          ...MSI_BLUE_TEXTURED_MUI_SWITCH_ROOT_SX,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState }) => {
          if (!ownerState.selected) {
            return {};
          }
          const c = ownerState.color;
          if (c === "primary" || c === "standard") {
            return msiBlueTexturedMuiToggleButtonPrimarySelectedSx();
          }
          return {};
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          ...MSI_BLUE_TEXTURED_MUI_SLIDER_PRIMARY_ROOT_SX,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        barColorPrimary: {
          ...MSI_BLUE_TEXTURED_MUI_LINEAR_PROGRESS_BAR_PRIMARY_SX,
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: ({ theme, ownerState }) => {
          if (ownerState.color !== "primary") {
            return {};
          }
          return {
            ...MSI_BLUE_TEXTURED_FILL_SX,
            color: "#fff",
            boxShadow: theme.shadows[6],
            "&:hover": {
              ...MSI_BLUE_TEXTURED_HOVER_BG_SX,
              boxShadow: theme.shadows[8],
            },
            "&:disabled": {
              backgroundColor: "rgba(27, 69, 132, 0.4)",
              backgroundImage: "none",
              color: "rgba(255,255,255,0.75)",
            },
          };
        },
      },
    },
  },
});

// Styled-components compatible theme
// styled-components expect theme.colors.primary, while MUI uses palette.primary.main
const styledComponentsTheme = {
  ...muiTheme,
  colors: {
    primary: muiTheme.palette.primary.main,
    secondary: muiTheme.palette.secondary.main,
    error: muiTheme.palette.error.main,
    warning: muiTheme.palette.warning.main,
    info: muiTheme.palette.info.main,
    success: muiTheme.palette.success.main,
    background: muiTheme.palette.background.default,
    paper: muiTheme.palette.background.paper,
    white: tokens.colors.bgCard.light,
    gray: tokens.colors.border.light,
    darkgray: tokens.colors.textSecondary.light,
    text: {
      primary: muiTheme.palette.text.primary,
      secondary: muiTheme.palette.text.secondary,
      disabled: muiTheme.palette.text.disabled,
    },
  },
};

// Default export for styled-components ThemeProvider
export default styledComponentsTheme;
